import { type JSX, useCallback } from 'react';
import { cx } from '@linaria/core';

import { type OddsFormat } from '@cx-modules/types';
import { OddsButton } from '@cx-modules/ui';
import { roundOdds, sendOddsClickCustomEvent } from '@cx-modules/utils';

import { useBetslip, useMatchesCouponContext } from '../../hooks';
import { type Bet, type Odd } from '../../types';
import { trackEvent } from '../../utils';

import { bestOddsButton, ButtonTextWrapper, Empty, LineWrapper, OddsTextWrapper, selectedBet } from './style';

interface BestOddsButtonsProps {
  bet: Bet;
  betLocation?: string;
  marketName?: string;
  subeventId: number;
  subeventName?: string;
}

const getOddText = (oddsFormat: OddsFormat, { american, decimal, fractional }: Odd) => {
  switch (oddsFormat) {
    case 'american':
      return american || '';
    case 'decimal':
      return decimal ? roundOdds(decimal) : '';
    case 'fractional':
      return fractional || '';
    default:
      return '';
  }
};

export const BestOddsButton = ({
  bet,
  betLocation = 'matches-coupon-odds-button',
  marketName,
  subeventId,
  subeventName,
}: BestOddsButtonsProps): JSX.Element => {
  const oddsFormat = useMatchesCouponContext(state => state?.user?.oddsFormat) || 'american';
  const renderBadge = useMatchesCouponContext(state => state.renderOddsButtonBadge);
  const bookmakersConfig = useMatchesCouponContext(state => state.bookmakersConfig);
  const onOddsButtonClick = useMatchesCouponContext(state => state.onOddsButtonClick);
  const analyticsDataLayer = useMatchesCouponContext(state => state.analyticsDataLayer);
  const analyticsImpressionEventData = useMatchesCouponContext(state => state.analyticsImpressionEventData);
  const eventId = useMatchesCouponContext(state => state.eventId);
  const eventName = useMatchesCouponContext(state => state.eventName);

  const { id, line, name, odds = [] } = bet;
  const bestOdds = odds?.[0];
  const { american, bookmakerCode } = bestOdds;
  const bookmakerConfig = bookmakersConfig?.[bookmakerCode];

  // NOTE: we default to American here if the helper can't return
  // but this may be incorrect, as elsewhere we default to SP
  const bestOddsText = getOddText(oddsFormat, bestOdds) || american;

  // Commented as non selected bets to currently have oddStable class and not show movement
  // To reinstate swap class oddStable for drifterSteamerClass in OddsButton around line 88 below
  // and uncomment below

  // const drifterSteamerClass = useMemo(() => {
  //   if (movement.toLowerCase() === 'drifting') {
  //     return oddDrifting;
  //   }
  //   if (movement.toLowerCase() === 'shortening') {
  //     return oddShortening;
  //   }
  //   return oddStable;
  // }, [movement]);

  const { isBookieBetSelected: isBetSelected, toggleBetslipBet } = useBetslip({
    betId: id,
    betLocation,
    betName: name,
    betOdds: bestOddsText,
    bookieCode: bookmakerCode,
    marketName,
    subeventName,
  });

  const handleOddsButtonClick = useCallback(() => {
    trackEvent(
      analyticsDataLayer ? 'widget_clickout' : 'matches_coupon_odds_click',
      {
        betId: id,
        betName: name,
        bookmakerCode,
        eventId,
        eventName,
        marketName,
        oddsText: bestOddsText,
        subeventId,
        subeventName,
        ...analyticsImpressionEventData,
      },
      analyticsDataLayer,
    );
    if (onOddsButtonClick) {
      return onOddsButtonClick('matches_coupon_click', id, bookmakerCode, name, bestOddsText);
    }
    sendOddsClickCustomEvent({
      betLocation,
      betName: name,
      betOdds: odds,
      bookieCode: bookmakerCode,
      event: 'odds_button_click',
      marketName,
      oddsFormat,
      subeventName,
    });
    toggleBetslipBet();
  }, [
    analyticsDataLayer,
    analyticsImpressionEventData,
    eventId,
    eventName,
    onOddsButtonClick,
    id,
    bestOddsText,
    betLocation,
    name,
    odds,
    bookmakerCode,
    marketName,
    oddsFormat,
    subeventId,
    subeventName,
    toggleBetslipBet,
  ]);

  const overUnderHelper = () => {
    if (name?.toLowerCase() === 'over') return 'O ';
    if (name?.toLowerCase() === 'under') return 'U ';
  };

  const lineName = line?.name;
  const hasLineName = Boolean(lineName);

  return typeof bestOddsText !== 'undefined' ? (
    <OddsButton
      bookieCode={bookmakerCode}
      bookmakerConfig={bookmakerConfig}
      className={cx(bestOddsButton, isBetSelected ? selectedBet : '')}
      isVerticalBadgeLogo={true}
      onClick={handleOddsButtonClick}
      renderBadge={renderBadge}
    >
      <ButtonTextWrapper>
        {hasLineName && (
          <LineWrapper key="line" useOddsFont>
            {overUnderHelper()}
            {lineName}
          </LineWrapper>
        )}
        <OddsTextWrapper key="odds" useLineFont={hasLineName}>
          {bestOddsText}
        </OddsTextWrapper>
      </ButtonTextWrapper>
    </OddsButton>
  ) : (
    <Empty>-</Empty>
  );
};
